import { useNavigate, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { toast, useDebounceCallback } from "@/components/kit";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import {
  useCreateCategory,
  useProductCategoriesQuery,
  useProductsQuery,
  useUpdateCategory,
} from "../api/queries";
import { productsApi } from "../api/client";
import {
  CategoryDialog,
  type CategoryFormValues,
} from "../ui/CategoryDialog";
import { ProductGrid } from "../ui/ProductGrid";
import { ProductsFilterPanel } from "../ui/ProductsFilterPanel";
import { ProductsPageHeader } from "../ui/ProductsPageHeader";
import { ProductsStats } from "../ui/ProductsStats";

export function ProductsPage() {
  const nav = useNavigate({ from: "/app/products" });
  const routeSearch = useSearch({ from: "/app/products" });
  const canManage = useActionPermission(["products:write"]);
  const categories = useProductCategoriesQuery();
  const [categoryId, setCategoryId] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [lowStockOnly, setLowStockOnly] = useState(
    routeSearch.lowStock === true,
  );
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(
    null,
  );
  const [exportingCsv, setExportingCsv] = useState(false);

  const debouncedSearch = useDebounceCallback((val: string) => {
    setSearch(val);
  }, 400);

  const list = useProductsQuery({
    categoryId: categoryId === "all" ? undefined : categoryId,
    search: search || undefined,
    lowStock: lowStockOnly || undefined,
  });

  const exportQuery = {
    categoryId: categoryId === "all" ? undefined : categoryId,
    search: search || undefined,
    lowStock: lowStockOnly || undefined,
  };

  const exportCsv = async () => {
    setExportingCsv(true);
    try {
      const blob = await productsApi.exportCsv(exportQuery);
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = `products-${new Date().toISOString().slice(0, 10)}.csv`;
      anchor.click();
      URL.revokeObjectURL(objectUrl);
      toast.success("ດາວໂຫລດ CSV ສຳເລັດ");
    } catch {
      toast.error("ສົ່ງອອກ CSV ບໍ່ສຳເລັດ");
    } finally {
      setExportingCsv(false);
    }
  };

  const createCategory = useCreateCategory();
  const updateCategory = useUpdateCategory();
  const categoryList = categories.data?.data ?? [];
  const isEditingCategory = editingCategoryId !== null;
  const categorySaving = createCategory.isPending || updateCategory.isPending;

  const editingCategory = editingCategoryId
    ? categoryList.find((c) => c.id === editingCategoryId)
    : undefined;

  const openCreateCategory = () => {
    setEditingCategoryId(null);
    setCategoryDialogOpen(true);
  };

  const openEditCategory = (id: string) => {
    if (!categoryList.find((c) => c.id === id)) return;
    setEditingCategoryId(id);
    setCategoryDialogOpen(true);
  };

  const submitCategory = (values: CategoryFormValues) => {
    const payload = {
      name: values.name,
      description: values.description || null,
      iconKey: values.iconKey,
    };
    const onSuccess = () => {
      setCategoryDialogOpen(false);
      setEditingCategoryId(null);
      toast.success(editingCategoryId ? "ອັບເດດຫມວດຫມູ່ແລ້ວ" : "ເພີ່ມຫມວດຫມູ່ແລ້ວ");
    };
    const onError = () => {
      toast.error("ບັນທຶກຫມວດຫມູ່ບໍ່ສຳເລັດ");
    };
    if (editingCategoryId) {
      updateCategory.mutate(
        { id: editingCategoryId, ...payload },
        { onSuccess, onError },
      );
    } else {
      createCategory.mutate(payload, { onSuccess, onError });
    }
  };

  const hasActiveFilters =
    categoryId !== "all" || search.length > 0 || lowStockOnly;

  const clearFilters = () => {
    setCategoryId("all");
    setSearchInput("");
    setSearch("");
    setLowStockOnly(false);
    nav({ search: { lowStock: undefined } });
  };

  const handleLowStockChange = (value: boolean) => {
    setLowStockOnly(value);
    nav({ search: { lowStock: value ? true : undefined } });
  };

  return (
    <>
      <Header />
      <Main className="flex flex-col gap-6">
        <ProductsPageHeader
          canManage={!!canManage}
          onCreate={() => nav({ to: "/app/products/new" })}
        />

        <ProductsStats
          products={list.data?.data ?? []}
          isLoading={list.isLoading}
        />

        <ProductsFilterPanel
          categories={categoryList}
          categoryId={categoryId}
          onCategoryChange={setCategoryId}
          searchValue={searchInput}
          onSearchChange={(val) => {
            setSearchInput(val);
            debouncedSearch(val);
          }}
          lowStockOnly={lowStockOnly}
          onLowStockChange={handleLowStockChange}
          canManage={!!canManage}
          onAddCategory={openCreateCategory}
          onEditCategory={openEditCategory}
          onClearFilters={clearFilters}
          hasActiveFilters={hasActiveFilters}
          onExportCsv={() => void exportCsv()}
          exportingCsv={exportingCsv}
        />

        <ProductGrid
          products={list.data?.data ?? []}
          isLoading={list.isLoading}
          canManage={!!canManage}
          onCreate={() => nav({ to: "/app/products/new" })}
        />

        <CategoryDialog
          open={categoryDialogOpen}
          onOpenChange={(open) => {
            setCategoryDialogOpen(open);
            if (!open) setEditingCategoryId(null);
          }}
          mode={isEditingCategory ? "edit" : "create"}
          initial={editingCategory ?? null}
          saving={categorySaving}
          onSubmit={submitCategory}
        />
      </Main>
    </>
  );
}
